import {
  OpenAPI,
} from '@zelos/codegen/apis';
import type { ApiRequestOptions } from '@zelos/codegen/apis/core/ApiRequestOptions';
import openApiSchema from '@zelos/codegen/openapi.json';
import axios from 'axios';
import { atom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { ServerDate } from './catch-server-date';
import { createStoreService } from './default-store.service';
import { isClientSide, isServerSide } from './environment';
import { refreshSession } from './refresh-session.service';

type AccessClaims = {
  exp: number,
  iat: number,
  jti: string,
  token_type: string,
  user_id: number,
};

const { atob } = globalThis || window;

const accessCookieAtom = atom<AccessCookie | null>(null);
const {
  get: getAccessCookie,
  set: setAccessCookie,
} = createStoreService(accessCookieAtom);

const getCookieValue = (cookie: string, name: string) => cookie.match(new RegExp(`${name}=([^;]+)`))?.[1];

class AccessCookie {
  #value: string | null;

  private constructor(raw: string | null) {
    this.#value = raw;
  }

  static parse(accessToken?: string | null) {
    const raw = (() => {
      if (accessToken) return accessToken;
      if (isClientSide) {
        const raw = getCookieValue(document.cookie, 'access');
        return raw || null;
      }
      return null;
    })();

    return new AccessCookie(raw);
  }

  private get exp() {
    const value = this.#value;
    if (!value) return 0;

    const [, payload] = value.split('.');
    try {
      const { exp }: AccessClaims = JSON.parse(
        atob(payload),
      );
      return exp;
    } catch {
      return 0;
    }
  }

  private get expirationTime() {
    return new Date(this.exp * 1000);
  }

  private needRefresh(cookies?: string | number | string[]) {
    let needRefresh: string | undefined;
    if (typeof cookies === 'string') {
      needRefresh = getCookieValue(cookies, 'need_refresh');
    } else if (Array.isArray(cookies)) {
      for (const cookie of cookies) {
        const needRefreshCookieValue = getCookieValue(cookie, 'need_refresh');
        if (needRefreshCookieValue) {
          needRefresh = needRefreshCookieValue;
          break;
        }
      }
    } else if (isClientSide) {
      needRefresh = getCookieValue(document.cookie, 'need_refresh');
    }
    if (!needRefresh) return false;
    if (!this.#value && needRefresh === 'true') return true;
    return false;
  }

  isExpired(at = new ServerDate()) {
    return this.expirationTime < at;
  }

  isNeedRefresh(cookies?: string | number | string[]) {
    return this.#value ? this.isExpired() : this.needRefresh(cookies);
  }

  toString() {
    return this.#value;
  }

  valueOf() {
    return this.#value;
  }
}

const isTokenEndpoints = (() => {
  const serialize = (path: string, operation: string) => (
    new URLSearchParams({ path, operation }).toString()
  );
  const tokenEndpoints = new Set<string>();

  Object.entries(openApiSchema.paths).forEach(([path, pathItemObject]) => {
    path.includes('/api/v1/users/token/')
    && Object.entries(pathItemObject).forEach(([operation]) => {
      tokenEndpoints.add(
        serialize(path, operation),
      );
    });
  });

  return ({ url, method }: ApiRequestOptions) => (
    tokenEndpoints.has(
      serialize(url, method.toLowerCase()),
    )
  );
})();

const fetchToken: typeof OpenAPI.TOKEN = async (apiRequestOptions) => {
  if (isTokenEndpoints(apiRequestOptions)) return '';

  getAccessCookie()?.isNeedRefresh()
  && await refreshSession();

  return getAccessCookie()?.valueOf() || '';
};

isClientSide && (OpenAPI.TOKEN = fetchToken);

export const resetAtomAccessCookie = () => {
  if (isServerSide) return;
  const newAccessCookie = AccessCookie.parse();
  newAccessCookie.valueOf() !== getAccessCookie()?.valueOf()
  && setAccessCookie(newAccessCookie);
};

isClientSide && resetAtomAccessCookie();

export const useAccessCookie = () => {
  const accessCookie = useAtomValue(accessCookieAtom);

  useEffect(() => {
    resetAtomAccessCookie();
  }, []);

  return isClientSide ? accessCookie : null;
};

const setDetectAccessCookie = () => {
  if (isServerSide) return;
  axios.interceptors.response.use((response) => {
    const documentAccessCookie = getCookieValue(document.cookie, 'access') || null;
    const localAccessCookie = getAccessCookie()?.valueOf() || null;
    documentAccessCookie !== localAccessCookie
    && resetAtomAccessCookie();
    return response;
  });
};

isClientSide && setDetectAccessCookie();

export const accessCookieClassRetrieveForTest = () => {
  if (process.env.NODE_ENV !== 'test')
    throw new Error('unreachable class "AccessCookie"');
  return {
    AccessCookie,
    accessCookieAtom: () => getAccessCookie(),
  };
};

export const accessCookieParserRetrieveForServerSide = (accessToken?: string | null) => {
  if (isClientSide)
    throw new Error('unreachable method "AccessCookie.parse"');
  return AccessCookie.parse(accessToken);
};
